import React from 'react'
import PropTypes from 'prop-types'
import Component from 'decorators/Component'
import styled from 'styled-components'
import { colors, margin, marginSizes } from 'theme/default'
import { isObjectEmpty } from 'tools/object'
import { H4, P } from 'components/Typo'
import Form, { FormContext, withFormContext, makeFormField } from 'components/Form'
import Flex from 'components/Flex'
import Card from 'components/Card'
import Divider from 'components/Divider'
import DivButton from 'components/DivButton'
import IconDelete from 'components/Icons/IconDelete'
import { stackOptions } from 'tools/promotion'
import Input from 'components/Input'
import { parseLocale } from 'tools/localisation'
import { useSoftRoute } from 'decorators/Route'
import { InlineNotification, notificationStates } from '@jarvis.oddle/core/components/InlineNotification'

const AppliedPromoCodeContainer = styled(Card).attrs(() => ({
  borderRadius: 'small',
  padding: 'medium'
}))`
  border: 1px ${props => props.theme.colorLinesAndBorders} solid;
  margin-bottom: ${margin[marginSizes.small]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:last-child {
    margin-bottom: 0;
  }
  /* stylelint-disable declaration-empty-line-before */
  ${props => props.disabled ? `
    color: ${props.theme.colorLinesAndBorders};
  ` : ''}
  /* stylelint-enable declaration-empty-line-before */
`
AppliedPromoCodeContainer.defaultProps = {
  theme: {
    colorLinesAndBorders: colors.separator
  }
}

const StyledSubmitButton = styled(Form.SubmitButton)`
  width: 100%;
  height: 100%;
`

const PromotionCodeErrorMessage = withFormContext(({ form: { errors, touched } }) =>
  errors.promoCode && touched.promoCode
    ? <Form.FieldError>{errors.promoCode}</Form.FieldError>
    : null
)

const FormInput = makeFormField(({ fieldError, fieldTouched, ...props }) => (
  <Flex flexBasis='100%' marginBottom={marginSizes.medium} marginRight={marginSizes.medium}>
    <Input {...props} withError={fieldError && fieldTouched} controlled />
  </Flex>
))

const PromoCodePromotion = Component()(({
  t,
  onPromotionSubmit = () => {},
  onPromotionDelete = () => {},
  selectedPromotions = [],
  metaState = {}
}) => {
  const route = useSoftRoute()
  const { locale } = route
  const { country } = parseLocale(locale)
  const { enableUniversalPromotion } = metaState.data.features || {}
  const { unstackable, selectedPromoCodes } = selectedPromotions.reduce((acc, selectedPromo) => {
    if (selectedPromo.stackableOption === stackOptions.nonStackable) {
      acc.unstackable = selectedPromo
    }

    if (selectedPromo.isPromoCodePromotion) {
      acc.selectedPromoCodes.push(selectedPromo)
    }

    return acc
  }, { unstackable: undefined, selectedPromoCodes: [] })

  return (
    <React.Fragment>
      <Form onSubmit={onPromotionSubmit}>
        <H4 normalised marginBottom={marginSizes.small} bold>{t('Have a promo code?')}</H4>
        <Flex alignItems='center'>
          <FormInput name='promoCode' label={t('Enter your promo code')} textTransform='uppercase' />
          <FormContext>
            {({ isSubmitting }) => (
              <Flex marginBottom={marginSizes.medium} flexShrink={0} flexBasis='100px'>
                <StyledSubmitButton data-testid='button.submit' disabled={isSubmitting || !isObjectEmpty(unstackable)} secondary>{t('Apply')}</StyledSubmitButton>
              </Flex>
            )}
          </FormContext>
        </Flex>
        <PromotionCodeErrorMessage />
      </Form>
      {
        country === 'SG' && enableUniversalPromotion && <InlineNotification
          lightTheme
          state={notificationStates.informative}
        >
          {t('Looking to use your Oddle Eats vouchers? Skip to payments in the checkout page and select your vouchers instead.')}
        </InlineNotification>
      }
      {selectedPromoCodes.length > 0 && (
        <React.Fragment>
          <Divider />
          <P bold>{t('Applied Promo Codes')}</P>
          {selectedPromoCodes.map((promotion) => (
            <AppliedPromoCodeContainer disabled={unstackable && unstackable.id !== promotion.id}>
              <Flex flexDirection='column'>
                <P normalised bold>{promotion.name}</P>
                <P normalised>{promotion.description}</P>
              </Flex>
              <DivButton data-testid='button.delete' onClick={() => onPromotionDelete({ promotion })}>
                <IconDelete />
              </DivButton>
            </AppliedPromoCodeContainer>
          ))}
        </React.Fragment>
      )}
    </React.Fragment>
  )
})

PromoCodePromotion.propTypes = {
  t: PropTypes.func,
  onPromotionSubmit: PropTypes.func,
  onPromotionDelete: PropTypes.func,
  selectedPromotions: PropTypes.array,
  metaState: PropTypes.object
}

export default PromoCodePromotion
